/**
 * @ngdoc service
 * @name runnerPublicFormService
 * @module flowingly.runner.services
 *
 * @description A helper service for public form related functionality
 *
 * ## Notes
 *
 * ###API
 * * getFormById - get the form data by form guid
 * * submitForm - submit the form and then will complete the first step under the hood
 */

import { SharedAngular } from '@Client/@types/sharedAngular';
import angular from 'angular';
import IRunnerCard from '@Shared.Angular/@types/core/contracts/queryModel/card/runnerCard';
import IApiPublicFormModel from '@Shared.Angular/@types/core/contracts/queryModel/api/apiPublicFormModel';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import { DEFAULT_PUBLIC_FORM_SUBJECT } from '@Shared.Angular/@types/core/contracts/constants/publicFormType';
import { Guid } from '@Shared.Angular/@types/guid';
import {
  FILE_UPLOAD_LIMIT_BYTES,
  FILE_UPLOAD_LIMIT_MB
} from '@Shared.Angular/flowingly.services/file.service';

export class RunnerPublicFormService {
  constructor(
    private $window: angular.IWindowService,
    private runnerPublicFormApiService: RunnerPublicFormApiService,
    private notificationService: SharedAngular.NotificationService,
    private fileService: SharedAngular.FileService
  ) {}

  getFormById(flowModelId: string) {
    return this.runnerPublicFormApiService
      .getFormById(flowModelId, this.inIframe(), false)
      .then((response) => {
        // validating the response result
        if (
          response === undefined ||
          response === null ||
          response.cardTasks === null ||
          response.fields === null ||
          response.stepName === null ||
          response.stepName === 'Invalid Form'
        ) {
          return undefined;
        }
        this.fileService.setUser(response.userId, response.businessId); // Need to setup the user and business in the File Service
        const cardTasks = JSON.parse(response.cardTasks) as IRunnerCard;
        const schema = { fields: [] };

        if (cardTasks !== null && cardTasks !== undefined) {
          schema.fields = cardTasks.formElements;
        }
        const publicForm = response as IRunnerApiPublicFormModel;
        publicForm.schema = schema;

        return publicForm;
      });
  }

  getExternalFormByStepId(stepId: Guid) {
    return this.runnerPublicFormApiService
      .getExternalFormByStepId(stepId, false)
      .then((data) => {
        // validating the response result
        if (!data) {
          return undefined;
        }
        this.fileService.setUser(data.userId, data.businessId); // Need to setup the user and business in the File Service
        const cardTasks = JSON.parse(data.cardTasks) as IRunnerCard;
        const schema: IFormSchema = { fields: [] };

        if (cardTasks !== null && cardTasks !== undefined) {
          schema.fields = cardTasks.formElements;

          schema.fields.forEach((schemaField) => {
            const matchingField = data.fields?.find(
              (field) => field.name === schemaField.name
            );

            if (matchingField?.notifyFields) {
              schemaField.notifyFields = matchingField.notifyFields;
            }
          });
        }
        const publicForm =
          data as IApiPublicFormModel as IRunnerApiPublicFormModel;
        publicForm.schema = schema;

        return publicForm;
      })
      .catch((x) => undefined);
  }

  saveForm(stepId: string, formData: any) {
    return this.runnerPublicFormApiService
      .saveForm(stepId, formData)
      .then((response) => {
        this.notificationService.showSuccessToast('Changes successfully saved');
      })
      .catch(() =>
        this.notificationService.showErrorToast('Something went wrong')
      );
  }

  submitForm(
    flowModelId: string,
    publicFormId: string,
    formData,
    stepId: Guid,
    isTestFlow = false
  ) {
    const postData = {
      publicFormId: publicFormId,
      cardData: JSON.stringify(formData),
      stepId: stepId,
      subject: DEFAULT_PUBLIC_FORM_SUBJECT
    };

    return this.runnerPublicFormApiService.submitForm(
      flowModelId,
      postData,
      false,
      isTestFlow
    );
  }

  submitExternalForm(stepId: Guid, formData) {
    const postData = {
      cardData: JSON.stringify(formData)
    };

    return this.runnerPublicFormApiService.submitExternalForm(
      stepId,
      postData,
      false
    );
  }

  uploadAnonymousFile(flowModelId, stepId, file, fieldKey) {
    if (file.size >= FILE_UPLOAD_LIMIT_BYTES) {
      this.notificationService.showErrorToast(
        `You cannot upload files greater than ${FILE_UPLOAD_LIMIT_MB}MB in size.`
      );
      return Promise.reject('');
    }

    const formData = new FormData(document.createElement('form'));
    const fileName = file.name.substr(file.name.lastIndexOf('\\') + 1);
    formData.append(fileName, file);

    return this.runnerPublicFormApiService.uploadAnonymousFile(
      formData,
      flowModelId,
      stepId,
      fieldKey
    );
  }

  inIframe() {
    try {
      return this.$window.self !== this.$window.top;
    } catch (e) {
      return true;
    }
  }
}

angular
  .module('flowingly.runner.services')
  .factory('runnerPublicFormService', [
    '$window',
    'runnerPublicFormApiService',
    'notificationService',
    'fileService',
    ($window, runnerPublicFormApiService, notificationService, fileService) =>
      new RunnerPublicFormService(
        $window,
        runnerPublicFormApiService,
        notificationService,
        fileService
      )
  ]);

export type RunnerPublicFormServiceType = InstanceType<
  typeof RunnerPublicFormService
>;

export interface IRunnerApiPublicFormModel extends IApiPublicFormModel {
  schema: IFormSchema;
}

export interface IFormSchema {
  fields: IFormField[];
}
